import { SparklesIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import { BUILTIN_PRINT_TEMPLATES, PAPER_IDS, PAPER_SIZES, createBlankPrintTemplateSpec, parsePrintTemplateSpec, type PaperId, type PaperOrientation } from 'shared-types';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { TextArea } from '../../components/inputs/TextArea';
import { Option, Select } from '../../components/inputs/Select';
import { Modal } from '../../components/Modal';
import { t } from '../../potree/translation/markers';
import { T, useT } from '../../translation/src';
import { usePrintTemplateCreateMutation, usePrintTemplateGenerateMutation } from '../../types/graphqlTypes';
import { AiErrorMessage } from '../objectEditor/ai/AiErrorMessage';
import { AiMessageLog } from '../objectEditor/ai/AiMessageLog';
import { AiProgress } from '../objectEditor/ai/AiProgress';
import { AiProgressTracker, useAiRunState } from '../objectEditor/ai/aiProgressTracker';
import { AI_MAX_PROMPT_LENGTH, aiErrorCode, type AiErrorCode } from '../objectEditor/ai/aiShared';
import { ReferenceImageField, useReferenceImage } from '../objectEditor/ai/ReferenceImageField';
import { useBuiltinTemplateText } from '../print/builtinTemplateText';
import type { PrintImageStore } from '../print/export/printImages';
import { TemplateThumbnail } from '../print/preview/TemplateThumbnail';
import type { PreviewEnv } from '../print/preview/usePreviewEnv';

const ORIENTATIONS: { id: PaperOrientation; label: string }[] = [
  { id: 'landscape', label: t('landscape') },
  { id: 'portrait', label: t('portrait') },
];

/** Describe it (the AI designs it: an ordinary template, edited by hand afterwards), a blank page, or a built-in. */
type Start = { kind: 'describe' } | { kind: 'blank' } | { kind: 'builtin'; id: string };

interface NewTemplateModalProps {
  open: boolean;
  onClose: () => void;
  organisationId: string;
  env: PreviewEnv | null;
  images: PrintImageStore | null;
  onCreated: (templateId: string) => void;
}

/** A new template: described (the AI designs it), a copy of a built-in to adapt, or a blank page of any paper size. */
const NewTemplateModal_: React.FC<NewTemplateModalProps> = ({ open, onClose, organisationId, env, images, onCreated }) => {
  // Describing it is the default, as for new objects.
  const [start, setStart] = useState<Start>({ kind: 'describe' });
  const [name, setName] = useState('');
  const [paper, setPaper] = useState<PaperId>('A3');
  const [orientation, setOrientation] = useState<PaperOrientation>('landscape');
  const [error, setError] = useState<string | null>(null);
  const [create, { loading: creating }] = usePrintTemplateCreateMutation({ refetchQueries: ['organisationTemplateLibrary'] });
  const [generate, { loading: generating }] = usePrintTemplateGenerateMutation();
  const loading = creating || generating;
  // Describe it: the request, an optional reference image (e.g. a photo of the title block in use), live progress.
  const [description, setDescription] = useState('');
  const reference = useReferenceImage();
  const [aiError, setAiError] = useState<AiErrorCode | null>(null);
  const [progress] = useState(() => new AiProgressTracker());
  const run = useAiRunState(progress);
  useEffect(() => () => progress.end(), [progress]);
  const describePlaceholder = useT('e.g. A3 landscape plan sheet with our logo top left, a title block bottom right with project, client, drawing number, scale and date, and a revision table above it', { swc: true });
  const failed = useT('the template could not be created', { swc: true });
  const blankName = useT('new template', { swc: true });
  const builtinText = useBuiltinTemplateText();
  const builtin = start.kind === 'builtin' ? BUILTIN_PRINT_TEMPLATES.find((b) => b.id === start.id) : undefined;
  const placeholder = builtin ? builtinText(builtin).name : blankName;
  const describing = start.kind === 'describe';
  const canSubmit = !describing || (description.trim() !== '' && !reference.reading);

  /** The AI designs the template; it is created as an ordinary draft and opened in the editor. */
  const generateAndCreate = async () => {
    setError(null);
    setAiError(null);
    let spec;
    let suggestedName: string | null = null;
    try {
      const progressChannel = await progress.begin();
      const result = await generate({ variables: { organisationId, prompt: description.trim(), image: reference.image, progressChannel } }).finally(() => progress.end());
      const generated = result.data?.printTemplateGenerate;
      const parsed = parsePrintTemplateSpec(generated?.spec);
      if (!generated || !parsed.ok) {
        setAiError('AI_INVALID_SPEC');
        return;
      }
      spec = parsed.spec;
      suggestedName = generated.name ?? null;
    } catch (e) {
      setAiError(aiErrorCode(e));
      return;
    }
    try {
      const result = await create({ variables: { organisationId, name: (name.trim() || suggestedName || description.trim()).slice(0, 80), spec } });
      const id = result.data?.printTemplateCreate.id;
      if (id) onCreated(id);
    } catch {
      setError(failed);
    }
  };

  const submit = async () => {
    if (describing) {
      await generateAndCreate();
      return;
    }
    setError(null);
    const spec = builtin ? builtin.spec : createBlankPrintTemplateSpec(paper, orientation);
    try {
      const result = await create({ variables: { organisationId, name: (name.trim() || placeholder).slice(0, 80), spec, basedOnBuiltinId: builtin?.id ?? null } });
      const id = result.data?.printTemplateCreate.id;
      if (id) onCreated(id);
    } catch {
      setError(failed);
    }
  };

  const card = (selected: boolean) =>
    classNames(
      'flex cursor-pointer flex-col gap-2 rounded-lg border-2 p-2 text-left text-xs transition-colors',
      selected ? 'border-french-violet-600 dark:border-neon-green-300' : 'border-gray-200 hover:border-gray-400 dark:border-gray-600',
    );

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="max-w-3xl w-full">
      <Modal.Header onClose={onClose}>
        <T _str="new template" swc />
      </Modal.Header>
      <div className="flex flex-col gap-4 text-left text-sm">
        <label className="flex flex-col gap-1">
          <T _str="name" swc />
          <Input value={name} placeholder={describing ? '' : placeholder} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <div>
          <div className="mb-2">
            <T _str="start from" swc />
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
            <button type="button" className={card(describing)} onClick={() => setStart({ kind: 'describe' })} disabled={loading}>
              <div className="flex h-24 flex-col items-center justify-center gap-1 rounded bg-french-violet-600/10 text-french-violet-600 dark:bg-neon-green-300/10 dark:text-neon-green-300">
                <SparklesIcon className="h-7 w-7" />
              </div>
              <span className="font-medium">
                <T _str="describe it" swc />
              </span>
            </button>
            <button type="button" className={card(start.kind === 'blank')} onClick={() => setStart({ kind: 'blank' })}>
              <div className="flex h-24 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">
                <T _str="blank page" swc />
              </div>
              <span className="font-medium">
                <T _str="blank page" swc />
              </span>
            </button>
            {BUILTIN_PRINT_TEMPLATES.map((b) => (
              <button key={b.id} type="button" className={card(start.kind === 'builtin' && start.id === b.id)} onClick={() => setStart({ kind: 'builtin', id: b.id })}>
                <TemplateThumbnail env={env} spec={b.spec} images={images} className="h-24 bg-gray-100 p-1 dark:bg-gray-800" />
                <span className="font-medium">{builtinText(b).name}</span>
              </button>
            ))}
          </div>
          {builtin && <p className="mt-2 text-xs text-gray-500">{builtinText(builtin).description}</p>}
        </div>
        {describing && (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <T _str="what should the template look like?" swc />
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onPaste={reference.onPaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit && !loading) {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder={describePlaceholder}
                maxLength={AI_MAX_PROMPT_LENGTH}
                disabled={loading}
                className="min-h-[6rem] text-sm"
              />
            </label>
            <ReferenceImageField state={reference} disabled={loading} />
            {run.running ? (
              <AiProgress run={run} hideThought subject="template" />
            ) : (
              <p className="text-xs text-gray-500">
                <T _str="the AI drafts the layout; you then edit it like any other template (paste or add a photo of an existing sheet to copy its layout)" swc />
              </p>
            )}
            {aiError && <AiErrorMessage code={aiError} subject="template" />}
          </div>
        )}
        {start.kind === 'blank' && (
          <div className="flex gap-3">
            <div className="w-48">
              <Select anchored value={paper} onChange={(e) => setPaper(e.target.value)} renderSelectedValue={(id) => PAPER_SIZES[id].label}>
                {PAPER_IDS.map((id) => (
                  <Option key={id} value={id}>
                    {PAPER_SIZES[id].label} ({PAPER_SIZES[id].widthMm} × {PAPER_SIZES[id].heightMm} mm)
                  </Option>
                ))}
              </Select>
            </div>
            <div className="w-40">
              <Select anchored value={orientation} onChange={(e) => setOrientation(e.target.value)} renderSelectedValue={(o) => <T _str={ORIENTATIONS.find((x) => x.id === o)?.label ?? o} swc />}>
                {ORIENTATIONS.map((o) => (
                  <Option key={o.id} value={o.id}>
                    <T _str={o.label} swc />
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        )}
        {error && <p className="text-error-red-500">{error}</p>}
      </div>
      {describing && run.running ? (
        // While the AI works, its messages come in here (read-only) instead of the buttons.
        <AiMessageLog run={run} className="mt-4" />
      ) : (
        <Modal.Footer className="justify-end gap-2">
          <Button variant="secondary" size="xs" onClick={onClose}>
            <T _str="cancel" swc />
          </Button>
          <Button variant="primary" size="xs" loading={loading} disabled={!canSubmit} onClick={() => void submit()}>
            {describing ? <T _str="generate and edit" swc /> : <T _str="create and edit" swc />}
          </Button>
        </Modal.Footer>
      )}
    </Modal.Container>
  );
};

export const NewTemplateModal = memo(NewTemplateModal_);
